import { isSubset } from 'es-toolkit';
// utils
import { IConversationsIntegration } from '@newo/api/gateway';

// key - integrationIdn, value - array of pairs (integrationIdn/connectorIdn)
type TIntegrationDictionary = Record<string, string[]>;

export const buildIntegrationsDictionary = (
    integrations: IConversationsIntegration[]
) => {
    return integrations.reduce<TIntegrationDictionary>(
        (accumulator, { integration, connectors }) => {
            accumulator[integration.idn] = connectors.map(
                ({ idn }) => `${integration.idn}/${idn}`
            );
            return accumulator;
        },
        {}
    );
};

const getSelectedValuePresent = (
    value: string,
    currentSelectedConnectors: string[]
): boolean => {
    const sliceSymbols = -2;
    return currentSelectedConnectors.some((selected) => {
        if (selected === value) {
            return true;
        }
        if (selected.endsWith('/*')) {
            const prefix = selected.slice(0, sliceSymbols);
            return value.startsWith(`${prefix}/`);
        }
        return value.startsWith(`${selected}/`);
    });
};

export const filterIntegrations = (
    integrations: IConversationsIntegration[],
    selectedConnectors: string[],
    searchTerm: string,
    isSelectedOnly: boolean
) => {
    return integrations.reduce<IConversationsIntegration[]>(
        (accumulator, integration) => {
            const connectors = integration.connectors.filter((connector) => {
                const value = `${integration.integration.idn}/${connector.idn}`;
                if (!connector.idn.includes(searchTerm)) {
                    return false;
                }

                if (isSelectedOnly) {
                    return getSelectedValuePresent(value, selectedConnectors);
                }

                return !getSelectedValuePresent(value, selectedConnectors);
            });

            if (connectors.length > 0) {
                accumulator.push({
                    ...integration,
                    connectors
                });
            }

            return accumulator;
        },
        []
    );
};

export const unFoldConnectors = (
    selectedConnectors: string[],
    integrationsDictionary: TIntegrationDictionary
) => {
    return selectedConnectors.reduce<string[]>((accumulator, pair) => {
        const [integrationIdn, connectorIdn] = pair.split('/');
        // if All connectors are selected
        if (connectorIdn === '*') {
            return accumulator.concat(
                integrationsDictionary[integrationIdn] ?? []
            );
        }
        return accumulator.concat(pair);
    }, []);
};

export const foldConnectors = (
    connectors: string[],
    integrationsDictionary: TIntegrationDictionary
) => {
    let remainingConnectors = Array.from(connectors);
    const updatedConnectors: string[] = [];

    const integrations = Object.keys(integrationsDictionary);

    for (const integration of integrations) {
        const requiredConnectors = integrationsDictionary[integration];

        const allPresent = isSubset(connectors, requiredConnectors);

        if (allPresent) {
            // If all elements are present, add "integration/*" to the updated array
            updatedConnectors.push(`${integration}/*`);

            // Filter out the replaced connectors from the remaining connectors array
            remainingConnectors = remainingConnectors.filter(
                (connector) => !requiredConnectors.includes(connector)
            );
        }
    }

    // Add any remaining connectors that were not replaced
    updatedConnectors.push(...remainingConnectors);

    return updatedConnectors;
};
